import { get, find } from "lodash"
import { isSamePortfolio } from "helpers/portfolios"
import { isSameWatchlist } from "helpers/watchlists"
import { safeDivide } from "helpers/math"
import moment from "moment"

export const NOTIFICATION_STATUSES = {
  ERROR: "ERROR",
  SUCCESS: "SUCCESS",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING: "WAITING",
  FILE_DOWNLOAD: "FILE_DOWNLOAD",
}

export const NOTIFICATION_TYPES = {
  PORTFOLIO_DOCTOR: "portfolio-doctor",
  FILE_DOWNLOAD: "file-download",
  ALPHA_THEORY_IMPORT: "alpha-theory-import",
  EZE_IMPORT: "eze-import",
}

export const computeSuccessNotification = (notif, swapUrlGetter, portfoliosLookup, portfolioUrlGetter, watchlistsLookup, watchlistUrlGetter, portfolioExperimentUrlGetter, researchTopicExperimentUrlGetter, experiments) => {
  const notification = { ...notif, status: NOTIFICATION_STATUSES.SUCCESS }
  const errorCount = notification.error

  if (notification.type === NOTIFICATION_TYPES.PORTFOLIO_DOCTOR) {
    if (notification.data.portfolioId) {
      const portfolioId = notification.data.portfolioId
      const portfolioPath = get(portfoliosLookup, [portfolioId, "alias"], portfolioId)
      const isSame = isSamePortfolio(window.location.pathname, portfolioPath, portfolioId)

      notification.message = errorCount > 0 ? "Portfolio upload is finished with some errors." : "Portfolio upload is finished."
      notification.targetName = get(portfoliosLookup, [portfolioId, "name"], "Unknown portfolio")
      notification.action = {
        text: "Go To Portfolio",
        fn: (navigate) => {
          if (isSame) {
            window.location.reload()
          } else {
            navigate(portfolioUrlGetter(portfolioPath))
          }
        },
      }
    } else if (notification.data.ticker) {
      notification.message = errorCount > 0 ? "Basket upload is finished with some errors." : "Basket upload is finished."
      notification.targetName = notification.data.ticker
      notification.action = {
        text: "Go To Basket",
        fn: (navigate) => {
          navigate(swapUrlGetter(notification.data.ticker))
        },
      }
    } else if (notification.data.experimentId) {
      let url
      const experimentId = notification.data.experimentId
      const experimentData = find(experiments, { id: experimentId })
      const experimentResourceType = get(experimentData, "resourceType")
      if (experimentResourceType === "RESEARCH_TOPIC") {
        url = researchTopicExperimentUrlGetter(experimentData.resourceId)
      } else if (experimentResourceType === "PORTFOLIO") {
        const id = get(portfoliosLookup, [experimentData.resourceId, "alias"], experimentData.resourceId)
        url = portfolioExperimentUrlGetter(id)
      }

      notification.message = errorCount > 0 ? "Alternative experiment upload is finished with some errors." : "Alternative experiment upload is finished."
      notification.targetName = get(experimentData, "name", "Unknown alternative experiment")
      // In the case the experiment wasn't found (the parent was deleted OR the experiment itself was deleted), do not show the Goto link
      if (url) {
        notification.action = {
          text: "Go To Experiment",
          fn: (navigate) => {
            navigate(url)
          },
        }
      }
    } else if (notification.data.watchlistId) {
      const watchlistId = notification.data.watchlistId
      const watchlistPath = get(watchlistsLookup, [watchlistId, "alias"], watchlistId)
      const isSame = isSameWatchlist(window.location.pathname, watchlistPath, watchlistId)

      notification.message = errorCount > 0 ? "Watchlist upload is finished with some errors." : "Watchlist upload is finished."
      notification.targetName = get(watchlistsLookup, [watchlistId, "name"], "Unknown watchlist")
      notification.action = {
        text: "Go To Watchlist",
        fn: (navigate) => {
          if (isSame) {
            window.location.reload()
          } else {
            navigate(watchlistUrlGetter(watchlistPath))
          }
        },
      }
    }
  } else if (notification.type === NOTIFICATION_TYPES.ALPHA_THEORY_IMPORT) {
    notification.message = errorCount > 0 ? "Alpha Theory import complete with some errors." : "Alpha Theory import complete."
    notification.targetName = notification.data.fundName
    notification.action = {
      text: "Go To Portfolio",
      fn: (navigate) => {
        navigate(notification.data.link)
      },
    }
  } else if (notification.type === NOTIFICATION_TYPES.FILE_DOWNLOAD) {
    notification.status = NOTIFICATION_STATUSES.FILE_DOWNLOAD
  } else if (notification.type === NOTIFICATION_TYPES.EZE_IMPORT) {
    notification.message = "Eze portfolio import is finished."
    notification.targetName = get(notification, "data.portfolioName", "Unknown portfolio")
  }

  return notification
}

export const computeErrorNotification = (notif, swapUrlGetter, portfoliosLookup, portfolioUrlGetter, watchlistsLookup, watchlistUrlGetter, researchTopicUrlGetter, experiments) => {
  const notification = { ...notif, status: NOTIFICATION_STATUSES.ERROR }

  if (notification.type === NOTIFICATION_TYPES.PORTFOLIO_DOCTOR) {
    if (notification.data.portfolioId) {
      const portfolioId = notification.data.portfolioId
      const portfolioPath = get(portfoliosLookup, [portfolioId, "alias"], portfolioId)

      notification.message = "Portfolio upload was not successful."
      notification.targetName = get(portfoliosLookup, [portfolioId, "name"], "Unknown portfolio")
      notification.action = {
        text: "Retry",
        fn: (navigate) => {
          navigate(`${portfolioUrlGetter(portfolioPath)}/upload`)
        },
      }
    } else if (notification.data.ticker) {
      notification.message = "Basket upload was not successful."
      notification.targetName = notification.data.ticker
      notification.action = {
        text: "Retry",
        fn: (navigate) => {
          navigate(`${swapUrlGetter(notification.data.ticker)}/upload`)
        },
      }
    } else if (notification.data.experimentId) {
      const experimentId = notification.data.experimentId
      const experimentData = find(experiments, { id: experimentId })
      const experimentResourceType = get(experimentData, "resourceType")
      let url
      if (experimentResourceType === "RESEARCH_TOPIC") {
        url = `${researchTopicUrlGetter(experimentData.resourceId)}/experiments/${experimentId}/upload`
      } else if (experimentResourceType === "PORTFOLIO") {
        const id = get(portfoliosLookup, [experimentData.resourceId, "alias"], experimentData.resourceId)
        url = `${portfolioUrlGetter(id)}/experiments/${experimentId}/upload`
      }

      notification.message = "Alternative experiment upload was not successful."
      notification.targetName = get(experimentData, "name", "Unknown alternative experiment")
      if (url) {
        notification.action = {
          text: "Retry",
          fn: (navigate) => {
            navigate(url)
          },
        }
      }
    } else if (notification.data.watchlistId) {
      const watchlistId = notification.data.watchlistId
      const watchlistPath = get(watchlistsLookup, [watchlistId, "alias"], watchlistId)

      notification.message = "Watchlist upload was not successful."
      notification.targetName = get(watchlistsLookup, [watchlistId, "name"], "Unknown watchlist")
      notification.action = {
        text: "Retry",
        fn: (navigate) => {
          navigate(`${watchlistUrlGetter(watchlistPath)}/upload`)
        },
      }
    }
  } else if (notification.type === NOTIFICATION_TYPES.ALPHA_THEORY_IMPORT) {
    notification.message = "Alpha Theory import failed."
    notification.targetName = notification.data.fundName
    notification.action = {
      text: "Contact Support",
      fn: () => {
        window.Intercom("showNewMessage", "Hello Omega Point, I am currently experiencing issues with the Alpha Theory import.")
      },
    }
  } else if (notification.type === NOTIFICATION_TYPES.FILE_DOWNLOAD) {
    notification.status = NOTIFICATION_STATUSES.FILE_DOWNLOAD
  } else if (notification.type === NOTIFICATION_TYPES.EZE_IMPORT) {
    notification.message = "Eze portfolio import was not successful."
    notification.targetName = get(notification, "data.portfolioName", "Unknown portfolio")
  }

  return notification
}

export const computeInProgressNotification = (notif, portfoliosLookup, watchlistsLookup, experiments, errorCount, successCount) => {
  const notification = { ...notif, status: NOTIFICATION_STATUSES.IN_PROGRESS }
  const total = get(notif, "total", 0)

  if (notification.type === NOTIFICATION_TYPES.PORTFOLIO_DOCTOR) {
    if (notification.data.portfolioId) {
      const portfolioId = notification.data.portfolioId
      notification.message = "Portfolio upload is in progress."
      notification.targetName = get(portfoliosLookup, [portfolioId, "name"], "Unknown portfolio")
    } else if (notification.data.ticker) {
      notification.message = "Basket upload is in progress."
      notification.targetName = notification.data.ticker
    } else if (notification.data.watchlistId) {
      const watchlistId = notification.data.watchlistId
      notification.message = "Watchlist upload is in progress."
      notification.targetName = get(watchlistsLookup, [watchlistId, "name"], "Unknown watchlist")
    } else if (notification.data.experimentId) {
      const experimentId = notification.data.experimentId
      const experimentData = find(experiments, { id: experimentId })

      notification.message = "Alternative experiment upload is in progress."
      notification.targetName = get(experimentData, "name", "Unknown alternative experiment")
    }
  } else if (notification.type === NOTIFICATION_TYPES.ALPHA_THEORY_IMPORT) {
    notification.message = "Importing data from Alpha Theory."
    notification.targetName = notification.data.fundName
  } else if (notification.type === NOTIFICATION_TYPES.FILE_DOWNLOAD) {
    notification.status = NOTIFICATION_STATUSES.FILE_DOWNLOAD
  } else if (notification.type === NOTIFICATION_TYPES.EZE_IMPORT) {
    notification.message = "Eze portfolio import is in progress."
    notification.targetName = get(notification, "data.portfolioName", "Unknown portfolio")
  }

  notification.progress = safeDivide(errorCount + successCount, total) * 100

  return notification
}

export const computeWaitingNotification = (notif, portfoliosLookup, watchlistsLookup, experiments) => {
  const notification = { ...notif, status: NOTIFICATION_STATUSES.WAITING }

  if (notification.type === NOTIFICATION_TYPES.PORTFOLIO_DOCTOR) {
    if (notification.data.portfolioId) {
      const portfolioId = notification.data.portfolioId
      notification.message = "Portfolio upload is queued."
      notification.targetName = get(portfoliosLookup, [portfolioId, "name"], "Unknown portfolio")
    } else if (notification.data.ticker) {
      notification.message = "Basket upload is queued."
      notification.targetName = notification.data.ticker
    } else if (notification.data.watchlistId) {
      const watchlistId = notification.data.watchlistId
      notification.message = "Watchlist upload is queued."
      notification.targetName = get(watchlistsLookup, [watchlistId, "name"], "Unknown watchlist")
    } else if (notification.data.experimentId) {
      const experimentId = notification.data.experimentId
      const experimentData = find(experiments, { id: experimentId })
      notification.message = "Alternative experiment upload is queued."
      notification.targetName = get(experimentData, "name", "Unknown alternative experiment")
    }
  } else if (notification.type === NOTIFICATION_TYPES.ALPHA_THEORY_IMPORT) {
    notification.message = "Alpha Theory import is queued."
    notification.targetName = notification.data.fundName
  } else if (notification.type === NOTIFICATION_TYPES.FILE_DOWNLOAD) {
    notification.status = NOTIFICATION_STATUSES.FILE_DOWNLOAD
  } else if (notification.type === NOTIFICATION_TYPES.EZE_IMPORT) {
    notification.message = "Eze portfolio import is queued."
    notification.targetName = get(notification, "data.portfolioName", "Unknown portfolio")
  }

  return notification
}

export function appendTimestamp(label, timestamp) {
  return `${label} - ${moment(timestamp).format("MMM D, YYYY @ h:mm A")}`
}
